import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { fetchMyProducts, fetchCreatorCapabilities, updateProduct, type CreatorProduct } from '@/services/creatorApi';
import { builderLabelStyle, builderHelpStyle } from '@/components/creation-flow/BuilderCard';

// Which of the creator's other products come with this membership. Shown only
// when pricing is a monthly subscription: the server grants every checked product
// on activation and pulls them again on cancel, and adding one here later grants
// it to everyone already subscribed.
//
// Bundling products into a membership is exclusive to founding Studio partners
// (Zak Barnett Studios, Oct 2026); other creators can still sell a single product
// monthly. Partners also set studio member prices on their other products here.
interface Props {
  value: string[];
  onChange: (ids: string[]) => void;
  excludeId?: string;
}

const kindLabel = (p: CreatorProduct) =>
  p.productType === 'webinar' ? 'Event' : p.productType === 'series' ? 'Course' : p.format === 'series' ? 'Series' : 'Video';

const IncludedProductsPicker: React.FC<Props> = ({ value, onChange, excludeId }) => {
  const [products, setProducts] = useState<CreatorProduct[] | null>(null);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  // Studio member price drafts, in dollars as typed, keyed by product id
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchCreatorCapabilities()
      .then(c => { if (alive) setAllowed(!!c.bundleMembership); })
      .catch(() => { if (alive) setAllowed(false); });
    fetchMyProducts()
      .then(list => { if (alive) setProducts(list.filter(p => p._id !== excludeId && p.pricingMode !== 'subscription')); })
      .catch(() => { if (alive) setProducts([]); });
    return () => { alive = false; };
  }, [excludeId]);

  const saveMemberPrice = async (p: CreatorProduct) => {
    const raw = (drafts[p._id] ?? '').trim();
    const cents = raw === '' ? null : Math.round(Number(raw) * 100);
    if (cents !== null && (!Number.isFinite(cents) || cents < 0 || (cents > 0 && cents < 100))) {
      setPriceError('Use $0 (free for members) or at least $1.');
      return;
    }
    setPriceError(null);
    setSaving(p._id);
    try {
      await updateProduct(p._id, { studioMemberPriceCents: cents as any });
      setProducts(prev => prev ? prev.map(x => x._id === p._id ? { ...x, studioMemberPriceCents: cents === null ? undefined : cents } : x) : prev);
      setDrafts(d => { const n = { ...d }; delete n[p._id]; return n; });
      setSavedId(p._id);
      setTimeout(() => setSavedId(id => (id === p._id ? null : id)), 2000);
    } catch (e: any) {
      setPriceError(e?.message || 'Could not save that price.');
    } finally {
      setSaving(null);
    }
  };

  // Not a partner (or still checking): no bundling option at all
  if (!allowed) return null;

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);

  return (
    <div>
      <Label style={builderLabelStyle}>Included with this membership</Label>
      <p style={{ ...builderHelpStyle, marginBottom: 8 }}>
        Subscribers get everything checked here for as long as they stay subscribed. Products you add later are granted to current subscribers too.
      </p>
      {products === null ? (
        <div className="flex items-center gap-2" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your products
        </div>
      ) : products.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', margin: 0 }}>
          Nothing to include yet. Create a class, course or video first and it will show up here.
        </p>
      ) : (
        <div
          style={{
            border: '1px solid var(--hj-beige-line)',
            borderRadius: 10,
            maxHeight: 220,
            overflowY: 'auto',
          }}
        >
          {products.map(p => {
            const on = value.includes(p._id);
            return (
              <label
                key={p._id}
                className="flex items-center gap-3"
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--hj-beige-line)',
                  fontSize: 13,
                  color: 'var(--hj-beige-ink)',
                }}
              >
                <input type="checkbox" checked={on} onChange={() => toggle(p._id)} style={{ accentColor: 'var(--hj-blue)' }} />
                <span className="flex-1 truncate">{p.title || 'Untitled'}</span>
                <span style={{ fontSize: 11, color: 'var(--hj-beige-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>
                  {kindLabel(p)}{p.published ? '' : ' · draft'}
                </span>
              </label>
            );
          })}
        </div>
      )}
      {value.length > 0 && (
        <p style={{ ...builderHelpStyle, marginTop: 6 }}>{value.length} included</p>
      )}

      {allowed && products && products.some(p => !value.includes(p._id)) && (
        <div style={{ marginTop: 16 }}>
          <Label style={builderLabelStyle}>Member prices on your other products</Label>
          <p style={{ ...builderHelpStyle, marginBottom: 8 }}>
            What subscribers pay for products not included above. Leave blank for no member price; $0 makes it free for members. Everyone else sees the member price as a reason to join.
          </p>
          <div style={{ border: '1px solid var(--hj-beige-line)', borderRadius: 10, maxHeight: 260, overflowY: 'auto' }}>
            {products.filter(p => !value.includes(p._id)).map(p => {
              const current = typeof p.studioMemberPriceCents === 'number' ? (p.studioMemberPriceCents / 100).toString() : '';
              const draft = drafts[p._id] ?? current;
              const dirty = draft.trim() !== current;
              return (
                <div key={p._id} className="flex items-center gap-2 flex-wrap" style={{ padding: '8px 12px', borderBottom: '1px solid var(--hj-beige-line)', fontSize: 13, color: 'var(--hj-beige-ink)' }}>
                  <span className="flex-1 truncate" style={{ minWidth: 120 }}>{p.title || 'Untitled'}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>
                    {typeof p.price === 'number' ? `$${(p.price / 100).toFixed(2).replace(/\.00$/, '')}` : ''}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--hj-beige-ink-2)' }}>Members $</span>
                  <input
                    id={`studio-price-${p._id}`}
                    type="number"
                    min={0}
                    step="1"
                    inputMode="decimal"
                    value={draft}
                    placeholder="—"
                    onChange={(e) => setDrafts(d => ({ ...d, [p._id]: e.target.value }))}
                    aria-label={`Member price for ${p.title || 'this product'}`}
                    style={{ width: 72, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--hj-beige-line)', background: 'transparent', color: 'var(--hj-beige-ink)', fontSize: 13 }}
                  />
                  <button
                    type="button"
                    disabled={!dirty || saving === p._id}
                    onClick={() => saveMemberPrice(p)}
                    style={{ padding: '4px 10px', borderRadius: 999, border: '1px solid var(--hj-beige-line)', background: dirty ? 'var(--hj-blue)' : 'transparent', color: dirty ? 'oklch(0.18 0.005 70)' : 'var(--hj-beige-ink-3)', fontSize: 12, fontWeight: 600, cursor: dirty ? 'pointer' : 'default' }}
                  >
                    {saving === p._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : savedId === p._id ? 'Saved' : 'Save'}
                  </button>
                </div>
              );
            })}
          </div>
          {priceError && <p style={{ fontSize: 12.5, color: 'hsl(var(--destructive))', margin: '6px 0 0' }}>{priceError}</p>}
        </div>
      )}
    </div>
  );
};

export default IncludedProductsPicker;
